import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import { useEffect, useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";

// Material Dashboard components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DataTable from "examples/Tables/DataTable";
import AlertBox from "components/Alert/alert";

// Data
import infoLinkTableData from "layouts/infoLink/data/infoLinkTableData";

// APIs
import { Box, CardContent, Tab, Tabs, Typography } from "@mui/material";
import colors from "assets/theme/base/colors";
import { getAllContinents } from "services/apiService";
import { getAllCountries } from "services/apiService";
import { addInfoLink } from "services/apiService";

function InfoLinkManagement() {
  const [alert, setAlert] = useState({ open: false, type: "success", title: "", message: "" });
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedTabName, setSelectedTabName] = useState("");
  const [editedName, setEditedName] = useState("");
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [continentsData, setContinentsData] = useState([]);
  const [tab, setTab] = useState(0);
  const [page, setPage] = useState(1);
  const [continentCountries, setContinentCountries] = useState([]);

  const { info } = colors;

  useEffect(() => {
    getContinents();
  }, []);

  const getContinents = async () => {
    try {
      const response = await getAllContinents();
      if (response?.success) {
        setContinentsData(response?.data);

        if (response?.data?.length > 0) {
          const defaultContinentName = response.data[0].name;
          getCountriesByContinent(defaultContinentName);
          setSelectedTabName(defaultContinentName);
        }
      }
    } catch (err) {
      console.error("Fetch users failed:", err);
    }
  };

  const getCountriesByContinent = async (continentName) => {
    try {
      const response = await getAllCountries(continentName);
      if (response?.success) {
        setContinentCountries(response.data || []);
      }
    } catch (err) {
      console.error("Fetch users failed:", err);
    }
  };

  const handleTabChange = (event, newValue) => {
    setTab(newValue);
    setPage(1); 

    const selectedContinent = continentsData[newValue];
    if (selectedContinent) {
      getCountriesByContinent(selectedContinent?.name);
      setSelectedTabName(selectedContinent?.name);
    }
  };

  const handleOpenEdit = (person) => {
    setSelectedItem(person);
    setEditedName(person?.infoLink);
    setOpenEditDialog(true);
  };

  const handleSaveEdit = async () => {
    if (!editedName.trim()) return;
    try {
      const formattedValue =
        editedName?.startsWith("http://") || editedName?.startsWith("https://")
          ? editedName
          : `https://${editedName}`;
      let obj = {
        countryId: selectedItem?.id,
        infoLink: formattedValue,
      };
      const response = await addInfoLink(obj);
      if (response?.success) {
        setAlert({
          open: true,
          type: "success",
          title: "Updated",
          message: response?.message,
        });
        getCountriesByContinent(selectedTabName);
      } else {
        setAlert({ open: true, type: "error", title: "Error", message: response?.message });
      }
    } catch (err) {
      setAlert({ open: true, type: "error", title: "Error", message: "Update failed." });
    } finally {
      setOpenEditDialog(false);
    }
  };

  const { columns, rows } = infoLinkTableData({
    peopleList: continentCountries,
    onEditClick: handleOpenEdit,
  });

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <AlertBox
        open={alert.open}
        type={alert.type}
        title={alert.title}
        message={alert.message}
        onClose={() => setAlert({ ...alert, open: false })}
      />

      <Dialog
        open={openEditDialog}
        onClose={() => setOpenEditDialog(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Edit Info Link</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            margin="dense"
            label="Info Link"
            value={editedName}
            onChange={(e) => setEditedName(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenEditDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSaveEdit}
            sx={{
              color: "beige",
            }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
              >
                <MDTypography variant="h6" color="white">
                  Information Links
                </MDTypography>
              </MDBox>
              <CardContent sx={{ padding: 3 }}>
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  Countries by Continent
                </Typography>
                <Tabs
                  value={tab}
                  onChange={handleTabChange}
                  variant="scrollable"
                  scrollButtons="auto"
                  allowScrollButtonsMobile
                  sx={{
                    "& .MuiTabs-flexContainer": {
                      display: "flex",
                      flexWrap: "nowrap",
                    },
                    "& .MuiTab-root": {
                      minWidth: "120px", 
                      flexShrink: 0, 
                    },
                    p: 1,
                    mt: 2,
                  }}
                  TabIndicatorProps={{ style: { backgroundColor: info.main, height: 3 } }}
                >
                  {continentsData.map((continent) => {
                    return (
                      <Tab
                        key={continent.name}
                        label={
                          <Box display="flex" flexDirection="column" alignItems="center">
                            <MDBox component="img" src={continent?.imageUrl} width="3rem" />
                            <Typography variant="subtitle2">{continent?.name}</Typography>
                          </Box>
                        }
                      />
                    );
                  })}
                </Tabs>
                <MDBox pt={3}>
                  <DataTable
                    table={{ columns, rows }}
                    isSorted={false}
                    entriesPerPage={{ defaultValue: 15, entries: [10, 15, 20, 35, 50] }}
                    showTotalEntries={true}
                    noEndBorder
                    canSearch={true}
                    page={page}
                    onPageChange={setPage}
                    searchFn={(user, query) => {
                      const lowerQuery = query?.toLowerCase();
                      return (
                        user?.country?.props?.children[1]?.props?.children
                          ?.toLowerCase()
                          .includes(lowerQuery) ||
                        String(user?.count?.props?.children)?.toLowerCase()?.includes(lowerQuery) ||
                        String(user?.link?.props?.children)?.toLowerCase()?.includes(lowerQuery)
                      );
                    }}
                  />
                </MDBox>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default InfoLinkManagement;
